import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Sparkles, ArrowRight, Inbox, FileText, GraduationCap, BarChart3,
  ShieldAlert, Quote, CheckCircle2, Menu, X, Plus, Minus, Lock, Users, Gauge, Building2,
} from "lucide-react";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_fd804437-99d8-4646-97e7-dd896c2b5f26/artifacts/avempq5s_ARULOGO-TM.png";
const HERO = "https://images.pexels.com/photos/6146971/pexels-photo-6146971.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CAMPUS = "https://images.unsplash.com/photo-1785190095889-86bb13040624?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const MODULES = {
  admissions: { icon: Inbox, title: "Admissions & Enquiry", desc: "Every applicant becomes a tracked case — completeness checks, routing and correspondence drafts — so nothing slips and decisions stay auditable.", points: ["Automated completeness checks", "Overdue-case dashboards", "One-click admit / revise / decline"] },
  concept: { icon: FileText, title: "Concept-Paper Gateway", desc: "Students submit against your rubric; the AI prepares a diagnostic; an authorised reviewer issues the official decision. AI prepares, humans decide.", points: ["Rubric-grounded AI diagnostic", "Approve / revise / decline workflow", "Full source traceability"] },
  supervision: { icon: GraduationCap, title: "Supervision Workspace", desc: "Version-controlled chapters, response-to-comments matrices, milestones and AI rubric checks — released only when the supervisor approves.", points: ["One chapter at a time, gated on approval", "Severity-tagged AI feedback", "Milestones & meeting notes"] },
  qa: { icon: BarChart3, title: "Quality & Performance", desc: "A live management view of turnaround times, supervisor load, human-control rates and escalations across your whole institution.", points: ["Workload & responsiveness metrics", "Human-in-the-loop analytics", "Institution-wide audit trail"] },
};

const FAQS = [
  ["Is the AI making decisions about my students?", "No. The platform is built on a strict human-in-the-loop model — the AI only prepares advisory, rubric-grounded diagnostics. Every binding decision (admit, approve, revise, decline) is made and owned by a qualified human."],
  ["Can we use our own guidelines and rubrics?", "Yes. Each institution is a fully isolated tenant with its own branding, users, referencing style and per-stage rubric criteria. The AI applies your rules; ARU's guidelines ship as the default."],
  ["How does it detect plagiarism and fake references?", "The assistant flags suspicious copied passages, fabricated-looking references and citation mismatches as advisory integrity signals — surfaced prominently to reviewers with the exact evidence, never as proof of misconduct."],
  ["Is our data kept separate from other institutions?", "Completely. Multitenancy enforces strict data isolation — users only ever see their own institution's records, and a platform super-admin onboards each new tenant."],
];

const STATS = [["1", "governed AI assistant per supervisor"], ["5", "thesis stages, reviewed one at a time"], ["100%", "of decisions made by humans"], ["24/7", "audit-ready traceability"]];

function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setSeen(true), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

export default function Landing() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("concept");
  const [faq, setFaq] = useState(0);
  const [menu, setMenu] = useState(false);
  const M = MODULES[tab];

  const nav = [["Platform", "#platform"], ["Modules", "#modules"], ["Integrity", "#integrity"], ["FAQ", "#faq"]];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2">
            <img src={LOGO} alt="Africa Research University" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <p className="font-heading font-extrabold text-sm">Africa Research University</p>
              <p className="text-[10px] text-muted-foreground tracking-wide">GROW · PROSPER · EXCEL</p>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {nav.map(([l, h]) => <a key={h} href={h} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{l}</a>)}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button data-testid="landing-signin" onClick={() => navigate("/login")} className="text-sm font-medium hover:brand-text transition-colors">Sign in</button>
            <button data-testid="landing-getstarted" onClick={() => navigate("/login")} className="brand-bg text-white text-sm px-4 py-2 rounded-full flex items-center gap-1 hover:opacity-90 transition-opacity">
              Launch platform <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <button className="md:hidden" data-testid="landing-menu" onClick={() => setMenu(!menu)}>{menu ? <X /> : <Menu />}</button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-border bg-white px-4 py-3 space-y-2">
            {nav.map(([l, h]) => <a key={h} href={h} onClick={() => setMenu(false)} className="block text-sm py-1">{l}</a>)}
            <button onClick={() => navigate("/login")} className="w-full brand-bg text-white text-sm px-4 py-2 rounded-full mt-2">Launch platform</button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl opacity-20 brand-bg" />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full blur-3xl opacity-20 brand-accent-bg" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5 brand-text" /> AI prepares · Humans decide
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Research supervision,<br /><span className="brand-text">scaled by humans</span><br />amplified by AI.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">
              A governance-first platform pairing every supervisor with an institutionally-controlled AI assistant — from admissions and concept papers to the final thesis chapter. Multitenant, auditable, and built for African universities.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-cta" onClick={() => navigate("/login")} className="brand-bg text-white px-6 py-3 rounded-full font-medium flex items-center gap-2 hover:opacity-90 transition-opacity">
                Get started <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#modules" className="px-6 py-3 rounded-full font-medium border border-border hover:bg-secondary transition-colors">Explore the platform</a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 brand-text" /> Human accountability</span>
              <span className="flex items-center gap-1"><Lock className="h-4 w-4 brand-text" /> Tenant data isolation</span>
              <span className="flex items-center gap-1"><Gauge className="h-4 w-4 brand-text" /> Full audit trail</span>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-border">
              <img src={HERO} alt="Students collaborating on research" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -left-4 top-10 bg-white rounded-2xl shadow-xl border border-border p-4 w-56 hidden sm:block">
              <div className="flex items-center gap-2 text-xs font-semibold"><Sparkles className="h-4 w-4 brand-text" /> AI Diagnostic</div>
              <p className="text-[11px] text-muted-foreground mt-1">Rubric-grounded findings prepared for your reviewer.</p>
              <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full brand-bg w-4/5" /></div>
            </div>
            <div className="absolute -right-3 bottom-8 bg-white rounded-2xl shadow-xl border-2 border-red-200 p-4 w-56 hidden sm:block">
              <div className="flex items-center gap-2 text-xs font-semibold text-red-700"><ShieldAlert className="h-4 w-4" /> Integrity signal</div>
              <p className="text-[11px] text-muted-foreground mt-1">Possible fabricated reference — flagged for human review.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Principle strip */}
      <section id="platform" className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-2 text-sm">
          {["Grow", "Prosper", "Excel"].map((w) => (
            <span key={w} className="font-heading font-bold text-lg brand-text">{w}</span>
          ))}
          <span className="text-muted-foreground">— a single platform for admissions, review, supervision & quality assurance.</span>
        </div>
      </section>

      {/* Modules — interactive tabs */}
      <section id="modules" className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">One platform, four connected modules</h2>
          <p className="mt-3 text-muted-foreground">Click a module to explore what the platform does at each stage of the research journey.</p>
        </div>
        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="flex flex-col gap-3">
            {Object.entries(MODULES).map(([k, m]) => {
              const active = k === tab;
              const Icon = m.icon;
              return (
                <button key={k} data-testid={`module-tab-${k}`} onClick={() => setTab(k)}
                  className={`text-left rounded-2xl border p-5 transition-all ${active ? "border-transparent brand-bg text-white shadow-lg scale-[1.01]" : "border-border bg-card hover:border-brand hover:-translate-y-0.5"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${active ? "bg-white/20" : "bg-secondary brand-text"}`}><Icon className="h-5 w-5" /></div>
                    <span className="font-heading font-semibold">{m.title}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div key={tab} className="rounded-3xl border border-border bg-card p-8 animate-fade-up">
            <div className="h-12 w-12 rounded-2xl brand-bg text-white flex items-center justify-center"><M.icon className="h-6 w-6" /></div>
            <h3 className="mt-4 font-heading text-2xl font-bold">{M.title}</h3>
            <p className="mt-2 text-muted-foreground">{M.desc}</p>
            <ul className="mt-5 space-y-2">
              {M.points.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 brand-text shrink-0" /> {p}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Integrity / plagiarism highlight */}
      <section id="integrity" className="bg-foreground text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-red-500/20 text-red-300 px-3 py-1 text-xs font-medium"><ShieldAlert className="h-3.5 w-3.5" /> Academic integrity</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold">Suspicious passages and fake references, surfaced.</h2>
            <p className="mt-3 text-white/70">The assistant scrutinises every submission for copied text, fabricated-looking references and citation mismatches — presenting each as a clear, evidence-backed integrity signal for the reviewer. Advisory only; the human always decides.</p>
            <ul className="mt-6 space-y-3">
              {[["Copied passages", "Abrupt tone or formatting shifts and uncited textbook-style definitions."],
                ["Fabricated references", "Implausible author / journal / DOI patterns and uncited list entries."],
                ["Citation mismatches", "In-text citations missing from the reference list, and vice-versa."]].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <ShieldAlert className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <div><p className="font-medium">{t}</p><p className="text-sm text-white/60">{d}</p></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border-2 border-red-400/40 bg-red-500/10 p-6">
            <div className="flex items-center gap-2 mb-3"><ShieldAlert className="h-5 w-5 text-red-300" /><span className="font-heading font-semibold">Integrity Signals — 2 flags for review</span></div>
            {[["Fabricated reference", "high", "Mwansa, J. (2023). Rural water equity. Journal of Pan-African Hydrology, 14(2).", "Journal and DOI could not be corroborated; pattern resembles a fabricated citation."],
              ["Copied passage", "medium", "\u201CWater is essential to all known forms of life and human civilisation depends upon it\u2026\u201D", "Register shifts sharply and the definition is uncited — possible unattributed source."]].map(([t, c, q, e], i) => (
              <div key={i} className="rounded-xl bg-white text-foreground p-4 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase text-red-700">{t}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${c === "high" ? "bg-red-600 text-white" : "bg-orange-200 text-orange-800"}`}>{c} confidence</span>
                </div>
                <div className="mt-2 flex gap-2 text-xs text-foreground/70"><Quote className="h-3 w-3 mt-0.5 text-red-500 shrink-0" /><span className="italic">{q}</span></div>
                <p className="mt-1 text-xs text-foreground/60">{e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center">How it works</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[["01", "Submit", "Students upload concept papers and chapters as PDF, Word or text.", FileText],
            ["02", "AI prepares", "The assistant runs your rubric and flags integrity signals.", Sparkles],
            ["03", "Human decides", "Supervisors review, adjust and release the official decision.", ShieldCheck],
            ["04", "Progress", "The next chapter unlocks only when the previous is approved.", GraduationCap]].map(([n, t, d, Icon]) => (
            <div key={n} className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform">
              <div className="flex items-center justify-between">
                <span className="font-heading text-3xl font-extrabold text-secondary-foreground/20">{n}</span>
                <Icon className="h-5 w-5 brand-text" />
              </div>
              <h3 className="mt-3 font-heading font-semibold">{t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats band */}
      <section className="relative overflow-hidden brand-bg text-white">
        <img src={CAMPUS} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map(([n, l]) => (
            <div key={l}>
              <p className="font-heading text-4xl sm:text-5xl font-extrabold">{n}</p>
              <p className="text-sm text-white/80 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-4 sm:px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center">Frequently asked</h2>
        <div className="mt-10 space-y-3">
          {FAQS.map(([q, a], i) => {
            const open = faq === i;
            return (
              <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`faq-item-${i}`}>
                <button onClick={() => setFaq(open ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
                  <span className="font-medium">{q}</span>
                  {open ? <Minus className="h-4 w-4 brand-text shrink-0" /> : <Plus className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>
                {open && <p className="px-5 pb-5 -mt-1 text-sm text-muted-foreground animate-fade-up">{a}</p>}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-20">
        <div className="rounded-3xl brand-bg text-white p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full brand-accent-bg opacity-30 blur-2xl" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold relative">Bring human-led AI supervision to your institution</h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto relative">Fully brandable and multitenant — onboard your university, set your rubrics, and start supervising at scale.</p>
          <button data-testid="cta-getstarted" onClick={() => navigate("/login")} className="relative mt-8 bg-white brand-text px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
            Get started <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card" data-testid="landing-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src={LOGO} alt="ARU" className="h-9 w-9 object-contain" />
              <span className="font-heading font-bold text-sm">Africa Research University</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Human-led, AI-supported research administration & supervision.</p>
            <p className="mt-3 text-xs brand-text font-medium">Grow · Prosper · Excel</p>
          </div>
          {[["Platform", [["Modules", "#modules"], ["Integrity", "#integrity"], ["How it works", "#faq"], ["Sign in", "/login"]]],
            ["Institution", [["Admissions", "#modules"], ["Supervision", "#modules"], ["Quality assurance", "#modules"], ["Multitenancy", "#platform"]]],
            ["Contact", [["info@aru.edu", "#"], ["Lusaka, Zambia", "#"], ["Request a demo", "/login"]]]].map(([h, items]) => (
            <div key={h}>
              <p className="font-heading font-semibold text-sm">{h}</p>
              <ul className="mt-3 space-y-2">
                {items.map(([l, href]) => <li key={l}><a href={href} className="text-sm text-muted-foreground hover:brand-text transition-colors">{l}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© {new Date().getFullYear()} Africa Research University. All rights reserved.</p>
            <p className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 brand-text" /> AI prepares · Humans decide</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
